import React, { useMemo } from 'react';
import FormLayoutRenderer from '../../components/generic/FormLayoutRenderer';
import { renderField, type FormLayoutVariant } from '../../components/generic/fieldRenderers';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { maybeEFormDisplayNode } from './DisplayField';
import { EFormFileField } from './FileField';
import { EFormPhotoField } from './PhotoField';
import { EFormSignatureField } from './SignatureField';
import { EFormSubformField } from './SubformField';
import {
  compileFormSchema,
  evaluateVisibleWhen,
  flattenSchemaFields,
  isEFormDisplayField,
  type EFormFieldSchema,
  type EFormSchema,
} from './schema';

export type EFormRuntimeProps = {
  schema: EFormSchema;
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  readOnly?: boolean;
  /** fieldKey → message */
  errors?: Record<string, string>;
  variant?: FormLayoutVariant;
  className?: string;
  /**
   * Optional chrome around each field node (e.g. builder diff highlighting).
   * Receives the rendered field element.
   */
  wrapField?: (fieldKey: string, node: React.ReactNode) => React.ReactNode;
  /**
   * Register/unregister inline subform validators (fieldKey → validate()).
   * Parent can call all registered fns before submit; see EFormSubformField.
   */
  onRegisterSubformValidate?: (
    fieldKey: string,
    validate: (() => boolean) | null,
  ) => void;
};

/** Compile schema and drop fields hidden by field-level `visibleWhen`. */
export function compileVisibleFormSchema(
  schema: EFormSchema,
  values: Record<string, unknown>,
) {
  const base = compileFormSchema(schema);
  const visibleKeys = new Set(
    flattenSchemaFields(schema)
      .filter((f) => evaluateVisibleWhen(f.visibleWhen, values))
      .map((f) => f.key),
  );
  return {
    fields: base.fields.filter((f) => visibleKeys.has(f.name)),
    formLayout: {
      ...base.formLayout,
      sections: (base.formLayout.sections ?? []).map((sec) => ({
        ...sec,
        fields: (sec.fields ?? []).filter((name) => visibleKeys.has(name)),
      })),
    },
  };
}

function FieldShell({
  fieldKey,
  children,
  error,
}: {
  fieldKey: string;
  children: React.ReactNode;
  error?: string;
}) {
  return (
    <div data-field-key={fieldKey} className={cn(error && 'scroll-mt-24')}>
      {children}
    </div>
  );
}

/**
 * Shared fill/preview field renderer for published forms and builder live preview.
 * Applies field-level `visibleWhen` filtering; section visibility stays in FormLayoutRenderer.
 */
export const EFormRuntime: React.FC<EFormRuntimeProps> = ({
  schema,
  values,
  onChange,
  readOnly = false,
  errors,
  variant = 'compact',
  className,
  wrapField,
  onRegisterSubformValidate,
}) => {
  const { colors } = useTheme();
  const compiled = useMemo(() => compileVisibleFormSchema(schema, values), [schema, values]);

  const schemaFieldsByKey = useMemo(() => {
    const map = new Map<string, EFormFieldSchema>();
    for (const f of flattenSchemaFields(schema)) map.set(f.key, f);
    return map;
  }, [schema]);

  const renderedFields: Record<string, React.ReactNode> = {};
  for (const field of compiled.fields) {
    const schemaField = schemaFieldsByKey.get(field.name);
    const fieldError = errors?.[field.name];
    let node: React.ReactNode;

    if (schemaField && isEFormDisplayField(schemaField)) {
      node = (
        <FieldShell fieldKey={field.name}>
          {maybeEFormDisplayNode(schemaField)}
        </FieldShell>
      );
    } else if (schemaField?.type === 'file') {
      node = (
        <FieldShell fieldKey={field.name} error={fieldError}>
          <label className={`block text-sm font-medium mb-1 ${colors.text}`}>
            {schemaField.label}
            {schemaField.required ? <span className="text-red-600"> *</span> : null}
          </label>
          <EFormFileField
            field={schemaField}
            value={values[field.name]}
            readOnly={readOnly || schemaField.readOnly}
            error={fieldError}
            onChange={(v) => {
              if (!readOnly) onChange(field.name, v);
            }}
          />
        </FieldShell>
      );
    } else if (schemaField?.type === 'photo') {
      node = (
        <FieldShell fieldKey={field.name} error={fieldError}>
          <label className={`block text-sm font-medium mb-1 ${colors.text}`}>
            {schemaField.label}
            {schemaField.required ? <span className="text-red-600"> *</span> : null}
          </label>
          {schemaField.helpText ? (
            <p className={`text-xs mb-1.5 ${colors.textMuted}`}>{schemaField.helpText}</p>
          ) : null}
          <EFormPhotoField
            field={schemaField}
            value={values[field.name]}
            readOnly={readOnly || schemaField.readOnly}
            error={fieldError}
            onChange={(v) => {
              if (!readOnly) onChange(field.name, v);
            }}
          />
        </FieldShell>
      );
    } else if (schemaField?.type === 'signature') {
      node = (
        <FieldShell fieldKey={field.name} error={fieldError}>
          <label className={`block text-sm font-medium mb-1 ${colors.text}`}>
            {schemaField.label}
            {schemaField.required ? <span className="text-red-600"> *</span> : null}
          </label>
          {schemaField.helpText ? (
            <p className={`text-xs mb-1.5 ${colors.textMuted}`}>{schemaField.helpText}</p>
          ) : null}
          <EFormSignatureField
            field={schemaField}
            value={values[field.name]}
            readOnly={readOnly || schemaField.readOnly}
            error={fieldError}
            onChange={(v) => {
              if (!readOnly) onChange(field.name, v);
            }}
          />
        </FieldShell>
      );
    } else if (schemaField?.type === 'subform') {
      node = (
        <FieldShell fieldKey={field.name} error={fieldError}>
          <label className={`block text-sm font-medium mb-1 ${colors.text}`}>
            {schemaField.label}
            {schemaField.required ? <span className="text-red-600"> *</span> : null}
          </label>
          <EFormSubformField
            field={schemaField}
            value={values[field.name]}
            readOnly={readOnly || schemaField.readOnly}
            error={fieldError}
            onRegisterValidate={
              onRegisterSubformValidate
                ? (fn) => onRegisterSubformValidate(field.name, fn)
                : undefined
            }
            onChange={(v) => {
              if (!readOnly) onChange(field.name, v);
            }}
          />
        </FieldShell>
      );
    } else {
      node = (
        <FieldShell fieldKey={field.name} error={fieldError}>
          {renderField({
            field: {
              ...field,
              disabled: readOnly || field.readOnly || field.disabled,
              readOnly,
            },
            value: values[field.name],
            error: fieldError,
            onChange: (name, value) => {
              if (!readOnly) onChange(name, value);
            },
            setFieldValue: (name, value) => {
              if (!readOnly) onChange(name, value);
            },
            getFormData: () => values,
            isEdit: true,
            formLayoutVariant: variant,
          })}
        </FieldShell>
      );
    }

    renderedFields[field.name] = wrapField ? wrapField(field.name, node) : node;
  }

  return (
    <div className={className}>
      <FormLayoutRenderer
        layout={compiled.formLayout}
        fields={renderedFields}
        formData={values}
        variant={variant}
      />
    </div>
  );
};

export default EFormRuntime;
